import { useFilterStore } from 'src/stores/filter';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { useInboxStore } from 'src/stores/inbox';
import { RuntimeStoreContext } from 'src/types/runtime/piniaRuntimeContext';

import { MODULE_LIST_TYPE } from 'src/models/genericActivityList';
import { StatusEnum } from 'src/models/status/enums';

import { today } from 'src/util/date';
import { registerListInstanceFetcher } from 'src/util/listInstance/listInstanceFetchDispatch';
import { registerModuleTabSettingsReset } from 'src/util/listInstance/moduleTabSettingsReset';
import { QueryStore } from 'src/util/query/QueryStore';

const warmupInboxList = (_store: RuntimeStoreContext, queryStore: QueryStore) => {
	const inboxStore = useInboxStore();

	inboxStore.clearSubscriptions({
		queryStore: queryStore,
	});

	void inboxStore.fetchOpenInboxList({
		queryStore: queryStore,
	});
};

const fetchInbox = (
	_store: RuntimeStoreContext,
	queryStore: QueryStore,
	listStore: ReturnType<typeof useInboxStore> = useInboxStore()
): void => {
	listStore.clearSubscriptions({
		queryStore: queryStore,
	});

	void listStore.fetchOpenInboxList({
		queryStore: queryStore,
	});
};

registerListInstanceFetcher('inbox', ({ listStore, queryStore, runtimeStore }) =>
	fetchInbox(runtimeStore as never, queryStore as never, listStore as never)
);

// Task 2.7 (reduzierter Shape, analog Task 2.3/2.5/2.6): Inbox trägt nur eine einzige Sub-Liste
// (Open) — Basis-Identifier muss `InboxList.vue`s `virtual-list-id="moduleActivityListScrollerInbox"`
// spiegeln (dort mit Open-Suffix versehen, s. GenericActivityListTabPanel.vue). Kein zentraler Export
// vorhanden, daher hier dieselbe Literal-Konstante wie in InboxList.vue.
const INBOX_SCROLL_BASE_IDENTIFIER = 'moduleActivityListScrollerInbox';

// Nur die Default-Instanz (Modul-Tabs laufen immer auf ihr) — `?list=`-Instanz-Tabs sind für inbox
// nicht erreichbar (kein Eintrag in SharedQueryRouteName/DesktopSharedQueryRouteName, s. Report) und
// disposen ohnehin vollständig über useListInstanceLifecycle. Feldliste 1:1 aus clearAllFilters
// (FilterComponentActions.ts) für 'inbox' abgeleitet, OHNE deren router.replace/refreshTheList:
// Box-/Benutzer-Filter, Datumsfilter. Inbox hat KEIN Zeitraum-Preset (setDateTimeRangeFilterForRoute
// trägt keinen 'inbox'-Case), KEINEN Richtungsfilter (isDirectionRoute() schließt 'inbox' aus) und
// KEINE Shared Query (isSharedQueryRoute() schließt 'inbox' aus) — anders als trashList/messageList/
// phoneCallList/mailList. Zusätzlich: Such-/Hidden-Suchbegriff, Mehrfachauswahl, Scroll-Position der
// einzigen Sub-Liste, aktive Ansicht (immer OPEN).
// NICHT angefasst: sort/listViewModeLarge (persistierte Präferenzen), Datenliste + Count + Pagination
// + queryStoreSubscriptions (Menü-Badges/Warmup lesen den Default-Store weiter — kein $reset/$dispose).
const resetInboxModuleTabSettings = (): void => {
	const inboxStore = useInboxStore();
	const filterStore = useFilterStore();
	const genericActivityListStore = useGenericActivityListStore();

	inboxStore.setFilteredBoxesAction({ boxIdFilter: [] });
	inboxStore.setFilteredUsersAction({ filterUsers: [] });
	inboxStore.setDateFilterAction({ dateFilter: today() });
	inboxStore.viewedUserId = null;

	filterStore.setSearchTerm({ module: 'inbox', searchTerm: '' });
	filterStore.setHiddenSearchTerm({ hiddenSearchTerm: '', module: 'inbox' });

	genericActivityListStore.clearTheSelection({ genericListIdentifier: 'inbox' });
	genericActivityListStore.resetScrollPositions(INBOX_SCROLL_BASE_IDENTIFIER);
	genericActivityListStore.setActiveView({
		activeView: MODULE_LIST_TYPE.OPEN,
		genericListIdentifier: 'inbox',
	});
};

registerModuleTabSettingsReset('inbox', resetInboxModuleTabSettings);

const deleteTheSelectedInboxAggregateIds = (
	_store: RuntimeStoreContext,
	_queryStore: QueryStore,
	queryParameters: { aggregateIds: string[]; processId: string }
) => {
	const inboxStore = useInboxStore();
	const genericActivityListStore = useGenericActivityListStore();

	inboxStore.deleteSelectedAggregateIds({
		aggregateIds: queryParameters.aggregateIds,
	});

	void genericActivityListStore.clearTheSelection({
		genericListIdentifier: 'inbox',
	});
};

const setTheStateForTheSelectedInboxAggregateIds = (
	_store: RuntimeStoreContext,
	_queryStore: QueryStore,
	queryParameters: {
		aggregateIds: string[];
		state: StatusEnum;
	}
) => {
	const inboxStore = useInboxStore();
	const genericActivityListStore = useGenericActivityListStore();

	switch (queryParameters.state) {
		case StatusEnum.UNSEEN:
			inboxStore.setTheInboxStateToUnseen({
				aggregateIds: queryParameters.aggregateIds,
			});
			break;
		case StatusEnum.SEEN:
			inboxStore.setTheInboxStateToSeen({
				aggregateIds: queryParameters.aggregateIds,
			});
			break;
		case StatusEnum.READY:
			inboxStore.setTheInboxStateToReady({
				aggregateIds: queryParameters.aggregateIds,
			});
			break;
		default:
			break;
	}

	void genericActivityListStore.clearTheSelection({
		genericListIdentifier: 'inbox',
	});
};

export { deleteTheSelectedInboxAggregateIds, fetchInbox, setTheStateForTheSelectedInboxAggregateIds, warmupInboxList };
